import withModal, { ModalProps } from '@components/ui-popup/modals/modal-hoc';
import { Grid, styled, Typography } from '@mui/material';
import { useAppTr } from '@i18n/use-app-tr';
import { PhoneNumber } from '@components/input/phone-number-field';
import WCTTextField from '@components/input/text-field';
import { LinkButton, OutlinedButton, PrimaryButton } from '@components/buttons';
import useChangePhoneNumber from '@pages/app/profile/forms/change-phone-number-hook';
import { PageAlertDisplay } from '@components/alerts';
import ActionsRow from '@components/layout-util-components/actions-row';
import { Gap } from '@components/layout-util-components/gap';

export interface ChangePhoneNumberModalProps extends ModalProps<string> {
  phone: PhoneNumber;
}

const Container = styled('form')({
  width: 600,
  display: 'flex',
  flexDirection: 'column',
});

function ChangePhoneNumberModal({
  phone,
  onClose,
}: ChangePhoneNumberModalProps) {
  const { t } = useAppTr('personalDetails');

  const { code, phoneNumber, onResendCode, canSubmit, isLoading } =
    useChangePhoneNumber(phone);

  const onSubmit = () => {
    onClose(code.value);
  };

  return (
    <Container
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <Typography variant="h2">{t('requestMobileCodeModal.title')}</Typography>
      <Gap size={24} />
      <Typography variant="body1">
        {t('requestMobileCodeModal.message', { phone: phoneNumber })}
      </Typography>
      <Gap size={20} />
      <Grid container alignItems="center" wrap="nowrap" gap={1}>
        <Grid item xs={6}>
          <WCTTextField
            name="code"
            label={t('requestMobileCodeModal.inputLabel')}
            value={code.value}
            error={code.error}
            onChange={code.set}
            onBlur={code.validate}
            maxLength={6}
            required
          />
        </Grid>
        <Grid item xs={6}>
          <LinkButton disabled={isLoading} onClick={onResendCode}>
            {t('requestMobileCodeModal.resendButtonLabel')}
          </LinkButton>
        </Grid>
      </Grid>
      <Gap size={24} />
      <PageAlertDisplay addMargin ignoreSuccess />
      <ActionsRow>
        <OutlinedButton onClick={() => onClose()}>
          {t('requestMobileCodeModal.cancelButtonLabel')}
        </OutlinedButton>
        <PrimaryButton disabled={!canSubmit} type="submit">
          {t('requestMobileCodeModal.continueButtonLabel')}
        </PrimaryButton>
      </ActionsRow>
    </Container>
  );
}

export default withModal(ChangePhoneNumberModal);
